import type { CSSProperties, ReactNode } from "react";
import { ArrowRight, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

/** Props for the IndexRow component. */
interface IndexRowProps {
  index: number;
  onClick: () => void;
  children: ReactNode;
  /** Colour class for the leading marker dot; defaults to the border tint. */
  markerClassName?: string;
  /** Quiet status text shown at rest; swapped for `action` on hover. */
  hint?: string;
  /** Verb shown beside the arrow on hover, naming what the click does. */
  action?: string;
  /** Whether the click's work is still in flight; swaps the arrow for a spinner. */
  isLoading?: boolean;
}

/**
 * A clickable index row prefixed with a numbered marker, styled as a line in
 * a table of contents. Staggers in on open; the nav arrow keys off hover.
 * @param props - The component props
 * @returns The index row
 */
export function IndexRow({
  index,
  onClick,
  children,
  markerClassName,
  hint,
  action,
  isLoading,
}: IndexRowProps) {
  const enter: CSSProperties = { animationDelay: `${index * 40}ms` };
  return (
    <button
      type="button"
      onClick={onClick}
      aria-busy={isLoading}
      style={enter}
      className="group/row flex w-full items-center gap-2.5 px-4 py-1.5 text-left text-sm text-foreground/90 transition-colors animate-in fade-in slide-in-from-left-1 fill-mode-both duration-300 hover:bg-accent hover:text-foreground motion-reduce:animate-none"
    >
      <span
        className={cn(
          "h-1 w-1 shrink-0 rounded-full transition-colors group-hover/row:bg-primary",
          markerClassName ?? "bg-border",
        )}
      />
      <span className="min-w-0 flex-1 break-words leading-snug">
        {children}
      </span>
      {hint && (
        <span className="shrink-0 text-[11px] text-muted-foreground group-hover/row:hidden">
          {hint}
        </span>
      )}
      {action && (
        <span className="hidden shrink-0 text-[11px] font-semibold text-primary group-hover/row:inline">
          {action}
        </span>
      )}
      {isLoading ? (
        <Loader2 className="h-3 w-3 shrink-0 animate-spin text-muted-foreground" />
      ) : (
        <ArrowRight className="h-3 w-3 shrink-0 -translate-x-1 self-center text-muted-foreground opacity-0 transition-all group-hover/row:translate-x-0 group-hover/row:opacity-100" />
      )}
    </button>
  );
}
